import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Footer from "../pages/Footer";
import "../css/InventoryDashboard.css";

import revenue_pic from "../images/revenue.png";
import profit_pic from "../images/profit.png";
import customers_pic from "../images/users.png";
import categories_pic from "../images/categories.png";
import products_pic from "../images/products.png";
import todays_orders_pic from "../images/todays_orders.png";
import total_orders_pic from "../images/total_orders.png";
import quantities_pic from "../images/quantities.png";

const InventoryDashboard = () => {
  const [data, setData] = useState({
    top5HighestSoldProducts: [],
    top5HighestProfitProducts: [],
    totalOrders: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [tempStartTime, setTempStartTime] = useState("");
  const [tempEndTime, setTempEndTime] = useState("");

  const stripRef = useRef(null);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value || 0);

  const formatDateFromEpoch = (epochTime) => {
    if (!epochTime) return "-";
    const date = new Date(Number(epochTime));
    return date.toLocaleString();
  };

  const convertToEpochTime = (dateString) => {
    if (!dateString) return "";
    const selectedDate = new Date(dateString);
    return selectedDate.getTime();
  };

  const retrieveInventoryData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url =
        startTime && endTime
          ? `https://goldenhides.in/api/inventory/data/time-range?startTime=${convertToEpochTime(
              startTime
            )}&endTime=${convertToEpochTime(endTime)}`
          : "https://goldenhides.in/api/inventory/dashboard";

      const response = await axios.get(url);
      if (response?.data) {
        setData(response.data);
      } else {
        setData({
          top5HighestSoldProducts: [],
          top5HighestProfitProducts: [],
          totalOrders: [],
        });
      }
    } catch (err) {
      console.error("Error fetching inventory data:", err);
      setError("Failed to load inventory data.");
    } finally {
      setLoading(false);
    }
  }, [startTime, endTime]);

  useEffect(() => {
    retrieveInventoryData();
  }, [retrieveInventoryData]);

  const getInventoryDataUsingTimeRange = (e) => {
    e.preventDefault();
    if (!tempStartTime || !tempEndTime) {
      alert("Please select Start Time and End Time");
      return;
    }
    setStartTime(tempStartTime);
    setEndTime(tempEndTime);
  };

  const getTodaysInventoryData = (e) => {
    e.preventDefault();
    setTempStartTime("");
    setTempEndTime("");
    setStartTime("");
    setEndTime("");
  };

  const downloadExcel = (orders) => {
    if (!orders || orders.length === 0) {
      alert("No Orders Found to Download Excel!!!");
      return;
    }

    const sheetData = orders.map((order) => ({
      "Order Id": order.orderId,
      "Order Time": formatDateFromEpoch(order.orderDate),
      "Customer Name": `${order.user?.firstName || ""} ${
        order.user?.lastName || ""
      }`.trim(),
      "Customer Email": order.user?.emailId || "",
      "Customer Mobile No": order.user?.phoneNo || "",
      Product: order.product?.title || "",
      Quantity: order.quantity,
      "Supplied Quantity": order.product?.suppliedQuantity || "",
      "Supplier Name": `${order.product?.supplier?.firstName || ""} ${
        order.product?.supplier?.lastName || ""
      }`.trim(),
      "Purchase Price": order.product?.purchasePrice || "",
      "Selling Price": order.product?.price || "",
      "Total Price": (order.quantity || 0) * (order.product?.price || 0),
      "Profit / Loss":
        (order.quantity || 0) *
        ((order.product?.price || 0) - (order.product?.purchasePrice || 0)),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(wb, `orders_${Date.now()}.xlsx`);
  };

  return (
    <div className="container-fluid mt-3 mb-5 inventory-dashboard">
      <div className="container">
        <header className="dashboard-header d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
          <h2 className="mb-0">Inventory Dashboard</h2>

          <form
            className="d-flex align-items-center gap-2 flex-wrap"
            onSubmit={getInventoryDataUsingTimeRange}
            aria-label="Inventory filter"
          >
            <div className="input-group">
              <label
                className="input-group-text visually-hidden"
                htmlFor="startTime"
              >
                Start Time
              </label>
              <input
                id="startTime"
                type="datetime-local"
                className="form-control"
                onChange={(e) => setTempStartTime(e.target.value)}
                value={tempStartTime}
                aria-label="Start Time"
              />
            </div>

            <div className="input-group">
              <label
                className="input-group-text visually-hidden"
                htmlFor="endTime"
              >
                End Time
              </label>
              <input
                id="endTime"
                type="datetime-local"
                className="form-control"
                onChange={(e) => setTempEndTime(e.target.value)}
                value={tempEndTime}
                aria-label="End Time"
              />
            </div>

            <button
              type="submit"
              className="btn bg-color custom-bg-text"
              disabled={loading}
            >
              {loading ? "Loading..." : "Get Inventory Data"}
            </button>

            <button
              type="button"
              className="btn btn-outline-secondary ms-1"
              onClick={getTodaysInventoryData}
            >
              Reset
            </button>
          </form>
        </header>

        {error && <div className="alert alert-danger mt-3">{error}</div>}

        <main className="mt-4">
          {/* KPI Cards */}
          <section className="row g-3">
            {[
              {
                img: revenue_pic,
                title: "Today's Sale",
                value: formatCurrency(data.totalSale),
              },
              {
                img: profit_pic,
                title: "Today's Profit",
                value: formatCurrency(data.totalProfit),
              },
              {
                img: total_orders_pic,
                title: "Today's Orders",
                value: data.totalTodaysOrdersCount ?? 0,
              },
              {
                img: quantities_pic,
                title: "Sold Products",
                value: data.totalProductsSold ?? 0,
              },
            ].map((item, idx) => (
              <div className="col-12 col-sm-6 col-md-3" key={idx}>
                <div className="card rounded-card h-100 kpi-card">
                  <div className="card-body d-flex gap-3 align-items-center">
                    <img src={item.img} alt={item.title} className="kpi-img" />
                    <div>
                      <div className="kpi-value">{item.value}</div>
                      <div className="text-muted small">{item.title}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Secondary KPIs */}
          {startTime === "" && endTime === "" && (
            <section className="row g-3 mt-3">
              {[
                {
                  img: customers_pic,
                  title: "Total Customers",
                  value: data.totalCustomerCount ?? 0,
                },
                {
                  img: categories_pic,
                  title: "Total Categories",
                  value: data.totalCategoryCount ?? 0,
                },
                {
                  img: products_pic,
                  title: "Total Products",
                  value: data.totalProductCount ?? 0,
                },
                {
                  img: todays_orders_pic,
                  title: "Total Orders",
                  value: data.totalOrderCount ?? 0,
                },
              ].map((item, idx) => (
                <div className="col-12 col-sm-6 col-md-3" key={`sec-${idx}`}>
                  <div className="card rounded-card h-100 kpi-card">
                    <div className="card-body d-flex gap-3 align-items-center">
                      <img
                        src={item.img}
                        alt={item.title}
                        className="kpi-img"
                      />
                      <div>
                        <div className="kpi-value">{item.value}</div>
                        <div className="text-muted small">{item.title}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </section>
          )}

          {/* Top Products & Tables */}
          <section className="row g-3 mt-3">
            <div className="col-lg-6">
              <div className="card rounded-card">
                <div className="card-body">
                  <h5 className="card-title">Top 5 Highest Sold Products</h5>
                  <div className="table-responsive small">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>Category</th>
                          <th>Sold Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top5HighestSoldProducts.length === 0 &&
                        !loading ? (
                          <tr>
                            <td colSpan="3" className="text-center text-muted">
                              No data
                            </td>
                          </tr>
                        ) : (
                          data.top5HighestSoldProducts.map((p, i) => (
                            <tr key={p.product?.id ?? `sold-${i}`}>
                              <td>
                                <strong>{p.product?.title ?? "-"}</strong>
                              </td>
                              <td>{p.product?.category?.title ?? "-"}</td>
                              <td>{p.totalSold ?? 0}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card rounded-card">
                <div className="card-body">
                  <h5 className="card-title">
                    Top 5 Highest Profitable Products
                  </h5>
                  <div className="table-responsive small">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>Category</th>
                          <th>Profit (Rs.)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top5HighestProfitProducts.length === 0 &&
                        !loading ? (
                          <tr>
                            <td colSpan="3" className="text-center text-muted">
                              No data
                            </td>
                          </tr>
                        ) : (
                          data.top5HighestProfitProducts.map((p, i) => (
                            <tr key={p.product?.id ?? `profit-${i}`}>
                              <td>
                                <strong>{p.product?.title ?? "-"}</strong>
                              </td>
                              <td>{p.product?.category?.title ?? "-"}</td>
                              <td>{formatCurrency(p.totalProfit ?? 0)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Orders Table */}
          <section className="row mt-4">
            <div className="col-12">
              <div className="card rounded-card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Total Customer Orders</h5>
                    <div>
                      <button
                        className="btn btn-sm bg-color custom-bg-text me-2"
                        onClick={() => downloadExcel(data.totalOrders)}
                        disabled={
                          !data.totalOrders || data.totalOrders.length === 0
                        }
                      >
                        Download Report
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive" style={{ maxHeight: 360 }}>
                    <table className="table table-hover table-striped mb-0 small">
                      <thead className="table-light">
                        <tr>
                          <th>Order Id</th>
                          <th>Order Time</th>
                          <th>Customer</th>
                          <th>Product</th>
                          <th>Quantity</th>
                          <th>Purchase Price</th>
                          <th>Selling Price</th>
                          <th>Profit / Loss</th>
                          <th>Total Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="9" className="text-center">
                              Loading...
                            </td>
                          </tr>
                        ) : data.totalOrders && data.totalOrders.length > 0 ? (
                          data.totalOrders.map((order) => {
                            const profit =
                              (order.quantity || 0) *
                              ((order.product?.price || 0) -
                                (order.product?.purchasePrice || 0));
                            const totalPrice =
                              (order.quantity || 0) *
                              (order.product?.price || 0);
                            return (
                              <tr key={order.orderId}>
                                <td>
                                  <strong>{order.orderId}</strong>
                                </td>
                                <td>{formatDateFromEpoch(order.orderDate)}</td>
                                <td>
                                  {`${order.user?.firstName ?? ""} ${
                                    order.user?.lastName ?? ""
                                  }`.trim()}
                                </td>
                                <td>{order.product?.title ?? "-"}</td>
                                <td>{order.quantity}</td>
                                <td>
                                  {formatCurrency(
                                    order.product?.purchasePrice ?? 0
                                  )}
                                </td>
                                <td>
                                  {formatCurrency(order.product?.price ?? 0)}
                                </td>
                                <td>{formatCurrency(profit)}</td>
                                <td>{formatCurrency(totalPrice)}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center text-muted">
                              No orders found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default InventoryDashboard;
